import { Box, Flex, Icon, Text, keyframes } from "@chakra-ui/react";
import NextLink from "next/link";
import { FC, useCallback, useState } from "react";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

type ProductFamily = "cdp" | "amp" | "aid";
type ProductStatus = "public" | "beta" | "private";

type Product = {
  name: string;
  description: string;
  href: string;
  family?: ProductFamily;
  status?: ProductStatus;
  docsReady?: boolean;
  badge?: string;
};

type Result = {
  recommended: Product;
  alsoRelevant?: Product[];
  startHere: { label: string; href: string }[];
};

type Branch = {
  question: string;
  options: {
    label: string;
    result: Result;
  }[];
};

type BranchKey = "sync" | "audiences" | "engage" | "personalize" | "ai";

const stepLabels = ["Choose your goal", "Narrow it down", "Your results"];

const StepIndicator: FC<{ current: number }> = ({ current }) => (
  <Flex align="center" gap={2} mb={4}>
    {stepLabels.map((label, i) => (
      <Flex key={label} align="center" gap={2}>
        {i > 0 && (
          <Box
            width="16px"
            height="1px"
            bg={i <= current ? "primary.border" : "base.border"}
          />
        )}
        <Flex align="center" gap={1.5}>
          <Box
            width="6px"
            height="6px"
            borderRadius="full"
            bg={i <= current ? "primary.base" : "base.border"}
            transition="background 200ms ease-out"
          />
          <Text
            fontSize="xs"
            fontWeight={i === current ? "semibold" : "normal"}
            color={i === current ? "primary.base" : "text.tertiary"}
            transition="color 200ms ease-out"
          >
            {label}
          </Text>
        </Flex>
      </Flex>
    ))}
  </Flex>
);

const tree: Record<BranchKey, Branch> = {
  sync: {
    question: "What kind of data are you sending?",
    options: [
      {
        label: "Warehouse data",
        result: {
          recommended: {
            name: "Reverse ETL",
            description:
              "Send modeled warehouse data to sales, support, marketing, and operational tools.",
            href: "/getting-started/concepts",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Customer Studio",
              description: "Build audiences on top of your warehouse data.",
              href: "/customer-studio/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "How data activation works", href: "/getting-started/concepts" },
            { label: "Destinations overview", href: "/destinations/overview" },
            { label: "Getting started for data teams", href: "/getting-started/data" },
          ],
        },
      },
      {
        label: "App or website events",
        result: {
          recommended: {
            name: "Hightouch Events",
            description:
              "Collect behavioral data from your app or website and send it to your tools.",
            href: "/events/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Real-Time Personalization",
              description: "Act on behavioral data immediately.",
              href: "/real-time/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Events overview", href: "/events/overview" },
            { label: "Getting started for data teams", href: "/getting-started/data" },
          ],
        },
      },
      {
        label: "Both",
        result: {
          recommended: {
            name: "Reverse ETL",
            description:
              "Start by syncing warehouse data to your tools, then add behavioral data collection.",
            href: "/getting-started/concepts",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Hightouch Events",
              description: "Collect behavioral data from your app or website.",
              href: "/events/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "How data activation works", href: "/getting-started/concepts" },
            { label: "Events overview", href: "/events/overview" },
          ],
        },
      },
    ],
  },
  audiences: {
    question: "Where will you use these audiences?",
    options: [
      {
        label: "Ad platforms",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Build audiences from warehouse data and send them to ad platforms.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Match Booster",
              description: "Improve match rates in ad platforms.",
              href: "/match-booster/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            { label: "Match Booster overview", href: "/match-booster/overview" },
            {
              label: "Getting started for business users",
              href: "/getting-started/business",
            },
          ],
        },
      },
      {
        label: "CRM and messaging tools",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Build audiences and send them to CRM, lifecycle, and messaging tools.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            {
              label: "Getting started for business users",
              href: "/getting-started/business",
            },
          ],
        },
      },
      {
        label: "Both",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Build audiences once and send them to both ad platforms and messaging tools.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Match Booster",
              description: "Improve match rates in ad platforms.",
              href: "/match-booster/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            { label: "Match Booster overview", href: "/match-booster/overview" },
          ],
        },
      },
    ],
  },
  engage: {
    question: "How do you want to manage your campaigns?",
    options: [
      {
        label: "I want to design the campaign flow myself",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Build audiences, design multi-step journeys, trigger messages from customer behavior, and measure results.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Intelligence",
              description:
                "Run experiments, compare audiences, and measure campaign lift.",
              href: "/campaign-intelligence/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            {
              label: "Intelligence overview",
              href: "/campaign-intelligence/overview",
            },
            {
              label: "Getting started for business users",
              href: "/getting-started/business",
            },
          ],
        },
      },
      {
        label: "I want AI to choose what each person gets",
        result: {
          recommended: {
            name: "AI Decisioning",
            description:
              "AI picks the best message, channel, and timing for each customer using reinforcement learning, then measures what worked.",
            href: "/ai-decisioning/overview",
            family: "aid",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Customer Studio",
              description: "Build the audiences that AI Decisioning optimizes.",
              href: "/customer-studio/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
          ],
        },
      },
      {
        label: "I'm not sure yet",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Start by building audiences and campaigns. You can layer in AI Decisioning later to automate per-customer decisions.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "AI Decisioning",
              description: "Add AI-driven per-customer optimization when you're ready.",
              href: "/ai-decisioning/overview",
              family: "aid",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
            {
              label: "Getting started for business users",
              href: "/getting-started/business",
            },
          ],
        },
      },
    ],
  },
  personalize: {
    question: "Where should personalization happen?",
    options: [
      {
        label: "On your website or in your app",
        result: {
          recommended: {
            name: "Real-Time Personalization",
            description:
              "Power onsite and in-app experiences with customer context and behavior.",
            href: "/real-time/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Hightouch Events",
              description: "Collect the behavioral data that drives personalization.",
              href: "/events/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Real-Time overview", href: "/real-time/overview" },
            { label: "Events overview", href: "/events/overview" },
          ],
        },
      },
      {
        label: "In emails, SMS, or push notifications",
        result: {
          recommended: {
            name: "AI Decisioning",
            description:
              "Let AI pick the best message, channel, and timing for each customer.",
            href: "/ai-decisioning/overview",
            family: "aid",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Customer Studio",
              description: "Build audiences to target with optimized messaging.",
              href: "/customer-studio/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
          ],
        },
      },
      {
        label: "Both",
        result: {
          recommended: {
            name: "Real-Time Personalization",
            description:
              "Start with onsite and in-app personalization, then add AI-driven messaging optimization.",
            href: "/real-time/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "AI Decisioning",
              description: "Let AI choose the best message, channel, and timing per customer.",
              href: "/ai-decisioning/overview",
              family: "aid",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Real-Time overview", href: "/real-time/overview" },
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
          ],
        },
      },
    ],
  },
  ai: {
    question: "What should AI help you with?",
    options: [
      {
        label: "Choosing what each customer gets",
        result: {
          recommended: {
            name: "AI Decisioning",
            description:
              "AI picks the best message, channel, and timing for each customer using reinforcement learning.",
            href: "/ai-decisioning/overview",
            family: "aid",
            status: "public",
            docsReady: true,
          },
          startHere: [
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
          ],
        },
      },
      {
        label: "Picking the right audience to target",
        result: {
          recommended: {
            name: "Customer Studio",
            description:
              "Build and refine audiences using an AI agent or visual builder, then run experiments to see which audiences perform best.",
            href: "/customer-studio/overview",
            family: "cdp",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Intelligence",
              description: "Run experiments and measure which audiences perform best.",
              href: "/campaign-intelligence/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
            {
              label: "Intelligence overview",
              href: "/campaign-intelligence/overview",
            },
          ],
        },
      },
      {
        label: "Both — audiences and what each person gets",
        result: {
          recommended: {
            name: "AI Decisioning",
            description:
              "Use AI to choose per-customer messaging, and Customer Studio to build and refine the audiences AI acts on.",
            href: "/ai-decisioning/overview",
            family: "aid",
            status: "public",
            docsReady: true,
          },
          alsoRelevant: [
            {
              name: "Customer Studio",
              description: "Use an AI agent or visual builder to create the audiences that AI Decisioning acts on.",
              href: "/customer-studio/overview",
              family: "cdp",
              status: "public",
              docsReady: true,
            },
          ],
          startHere: [
            { label: "AI Decisioning overview", href: "/ai-decisioning/overview" },
            { label: "Customer Studio overview", href: "/customer-studio/overview" },
          ],
        },
      },
    ],
  },
};

const q1Options: { label: string; key: BranchKey }[] = [
  { label: "Sync data to my business tools", key: "sync" },
  { label: "Build audiences and send them to tools", key: "audiences" },
  { label: "Engage customers across channels", key: "engage" },
  { label: "Personalize website and app experiences", key: "personalize" },
  { label: "Let AI decide what each customer gets", key: "ai" },
];

type Step =
  | { phase: "q1" }
  | { phase: "q2"; branchKey: BranchKey }
  | { phase: "result"; branchKey: BranchKey; result: Result };

const OptionButton: FC<{
  children: string;
  onClick: () => void;
}> = ({ children, onClick }) => (
  <Flex
    as="button"
    onClick={onClick}
    align="center"
    width="100%"
    textAlign="left"
    border="1px solid"
    borderColor="base.border"
    borderRadius="xl"
    padding={4}
    bg="white"
    cursor="pointer"
    boxShadow="none"
    transition="background-color 150ms ease-out"
    _hover={{
      bg: "base.lightBackground",
    }}
    _focusVisible={{ boxShadow: "outline", outline: "none" }}
  >
    <Text fontSize="md" color="text.primary" fontWeight="medium" flex="1">
      {children}
    </Text>
    <Icon
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      color="text.tertiary"
      boxSize="16px"
      flexShrink={0}
      ml={3}
      transition="color 150ms ease-out, transform 150ms ease-out"
      sx={{ "button:hover &": { color: "primary.base", transform: "translateX(2px)" } }}
    >
      <path d="M9 18l6-6-6-6" />
    </Icon>
  </Flex>
);

const BackButton: FC<{
  children: string;
  onClick: () => void;
}> = ({ children, onClick }) => (
  <Box
    as="button"
    onClick={onClick}
    color="text.tertiary"
    fontSize="sm"
    cursor="pointer"
    borderRadius="base"
    outline="none"
    _hover={{ color: "primary.base" }}
    _focusVisible={{ boxShadow: "outline" }}
    bg="transparent"
    border="none"
    padding={0}
  >
    {children}
  </Box>
);

const ProductCard: FC<{
  product: Product;
  isPrimary?: boolean;
}> = ({ product, isPrimary = false }) => (
  <NextLink passHref href={product.href}>
    <Flex
      as="a"
      direction="column"
      border="1px solid"
      borderColor={isPrimary ? "primary.border" : "base.border"}
      borderRadius="xl"
      padding={isPrimary ? 5 : 4}
      bg={isPrimary ? "primary.background" : "white"}
      boxShadow="none"
      textDecoration="none"
      cursor="pointer"
      _hover={{
        textDecoration: "none",
        bg: isPrimary ? "primary.backgroundHover" : "base.lightBackground",
      }}
      _focusVisible={{ boxShadow: "outline", outline: "none" }}
      transition="background-color 150ms ease-out"
    >
      <Text
        fontFamily="heading"
        fontWeight="semibold"
        fontSize={isPrimary ? "lg" : "md"}
        color="text.primary"
        mb={1}
      >
        {product.name}
        {product.badge && (
          <Text
            as="span"
            fontSize="xs"
            fontWeight="medium"
            color="text.tertiary"
            ml={2}
          >
            {product.badge}
          </Text>
        )}
      </Text>
      <Text fontSize="sm" color="text.secondary" lineHeight="1.6" flex="1">
        {product.description}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="primary.base"
        mt={2}
        display="flex"
        alignItems="center"
        gap={1}
        transition="color 150ms ease-out"
        sx={{ "a:hover &": { color: "primary.hover" } }}
      >
        {isPrimary ? "Get started" : "Learn more"}
        <Box
          as="span"
          transition="transform 150ms ease-out"
          sx={{ "a:hover &": { transform: "translateX(4px)" } }}
        >
          →
        </Box>
      </Text>
    </Flex>
  </NextLink>
);

export const ProductFinder: FC = () => {
  const [step, setStep] = useState<Step>({ phase: "q1" });

  const handleQ1 = useCallback((key: BranchKey) => {
    setStep({ phase: "q2", branchKey: key });
  }, []);

  const handleQ2 = useCallback(
    (branchKey: BranchKey, result: Result) => {
      setStep({ phase: "result", branchKey, result });
    },
    [],
  );

  const handleBack = useCallback(() => {
    if (step.phase === "q2") {
      setStep({ phase: "q1" });
    } else if (step.phase === "result") {
      setStep({ phase: "q2", branchKey: step.branchKey });
    }
  }, [step]);

  const handleReset = useCallback(() => {
    setStep({ phase: "q1" });
  }, []);

  return (
    <Box
      id="product-finder"
      border="1px solid"
      borderColor="base.border"
      borderRadius="xl"
      padding={6}
      bg="base.lightBackground"
      boxShadow="none"
      my={8}
    >
      {step.phase === "q1" && (
        <Box animation={`${fadeIn} 200ms ease-out`}>
          <StepIndicator current={0} />
          <Text
            fontFamily="heading"
            fontWeight="semibold"
            fontSize="lg"
            color="text.primary"
            mb={1}
          >
            Which product should I start with?
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={5}>
            Answer two quick questions and we will point you to the right
            starting point.
          </Text>
          <Flex direction="column" gap={3}>
            {q1Options.map((opt) => (
              <OptionButton key={opt.key} onClick={() => handleQ1(opt.key)}>
                {opt.label}
              </OptionButton>
            ))}
          </Flex>
          <Flex
            mt={4}
            pt={4}
            borderTop="1px solid"
            borderTopColor="base.border"
            align="center"
            gap={2}
          >
            <Icon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              color="text.tertiary"
              boxSize="14px"
              flexShrink={0}
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              <path d="M9 12l2 2 4-4" />
            </Icon>
            <NextLink passHref href="/workspace-management/governance">
              <Text
                as="a"
                fontSize="sm"
                color="text.secondary"
                borderRadius="base"
                outline="none"
                _hover={{ color: "primary.base", textDecoration: "underline" }}
                _focusVisible={{ boxShadow: "outline" }}
              >
                Need help with setup, permissions, or approvals? Start with
                governance and admin docs.
              </Text>
            </NextLink>
          </Flex>
        </Box>
      )}

      {step.phase === "q2" && (
        <Box animation={`${fadeIn} 200ms ease-out`}>
          <StepIndicator current={1} />
          <Flex align="center" gap={2} mb={1}>
            <BackButton onClick={handleBack}>← Back</BackButton>
          </Flex>
          <Text
            fontFamily="heading"
            fontWeight="semibold"
            fontSize="lg"
            color="text.primary"
            mb={1}
          >
            {tree[step.branchKey].question}
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={5}>
            This helps us narrow down the best starting point.
          </Text>
          <Flex direction="column" gap={3}>
            {tree[step.branchKey].options.map((opt) => (
              <OptionButton
                key={opt.label}
                onClick={() => handleQ2(step.branchKey, opt.result)}
              >
                {opt.label}
              </OptionButton>
            ))}
          </Flex>
        </Box>
      )}

      {step.phase === "result" && (
        <Box animation={`${fadeIn} 200ms ease-out`}>
          <StepIndicator current={2} />
          <Flex align="center" gap={3} mb={4}>
            <BackButton onClick={handleBack}>← Back</BackButton>
            <Text fontSize="sm" color="text.tertiary">
              ·
            </Text>
            <BackButton onClick={handleReset}>Start over</BackButton>
          </Flex>

          <Text
            fontFamily="heading"
            fontWeight="semibold"
            fontSize="lg"
            color="text.primary"
            mb={4}
          >
            Recommended for you
          </Text>

          <ProductCard product={step.result.recommended} isPrimary />

          {step.result.alsoRelevant && step.result.alsoRelevant.length > 0 && (
            <Box mt={4}>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="text.secondary"
                mb={2}
              >
                Also relevant
              </Text>
              <Flex direction="column" gap={2}>
                {step.result.alsoRelevant.map((p) => (
                  <ProductCard key={p.name} product={p} />
                ))}
              </Flex>
            </Box>
          )}

          <Box mt={5}>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="text.secondary"
              mb={2}
            >
              Start here
            </Text>
            <Flex direction="column" gap={2}>
              {step.result.startHere.map((link) => (
                <NextLink key={link.href} passHref href={link.href}>
                  <Flex
                    as="a"
                    align="center"
                    gap={1.5}
                    fontSize="sm"
                    color="primary.base"
                    textDecoration="none"
                    borderRadius="base"
                    outline="none"
                    _hover={{ color: "primary.hover", textDecoration: "underline" }}
                    _focusVisible={{ boxShadow: "outline" }}
                  >
                    <Box as="span" fontSize="xs">→</Box>
                    {link.label}
                  </Flex>
                </NextLink>
              ))}
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
};
